import React, { useState, useEffect } from 'react';
import './ProfileModal.css';
import { Icons } from '../Icons/Icons';
import t from '../../i18n/useTranslation';
import ui from '../../i18n/ui';
import { Capacitor } from '@capacitor/core';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { getInterestLabel } from '../../constants/interests';
import ImageViewer from '../ImageViewer/ImageViewer';
import PlanBadge from '../Plans/PlanBadge';
import InterestPickerModal from '../InterestPicker/InterestPickerModal';
import { getSortedCountries, getRegions } from '../../data/countriesAndRegions';
import useModalClose from '../../hooks/useModalClose';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api';

function ProfileModal({ currentUser, onClose, onUpdate }) {
    // ESC + Android back cierran el modal
    useModalClose(onClose, true);
    const [name, setName] = useState(currentUser.name || '');
    const [status, setStatus] = useState(currentUser.status || '');
    const [language, setLanguage] = useState(currentUser.language || 'español');
    const [region, setRegion] = useState(currentUser.region || '');
    const [country, setCountry] = useState(currentUser.country || '');
    const [interests, setInterests] = useState(Array.isArray(currentUser.interests) ? currentUser.interests : []);
    const [voice, setVoice] = useState(currentUser.voice || '');
    const [availableVoices, setAvailableVoices] = useState([]);
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(currentUser.avatar || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [fullAvatar, setFullAvatar] = useState(null);
    const [showInterestPicker, setShowInterestPicker] = useState(false);

    const removeInterest = (key) => {
        setInterests(prev => prev.filter(i => i !== key));
    };

    const langToBcp = {
        'español': 'es', 'inglés': 'en', 'francés': 'fr', 'alemán': 'de',
        'portugués': 'pt', 'italiano': 'it', 'euskera': 'es', 'chino': 'zh',
        'japonés': 'ja', 'árabe': 'ar', 'hindi': 'hi', 'ruso': 'ru',
        'coreano': 'ko', 'turco': 'tr', 'indonesio': 'id'
    };

    // Cargar voces del dispositivo (web + móvil Capacitor)
    useEffect(() => {
        let isNative = false;
        try { isNative = Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform(); } catch (e) {}

        if (isNative) {
            (async () => {
                try {
                    const result = await TextToSpeech.getSupportedVoices();
                    const raw = (result && result.voices) || [];
                    const voices = raw.map(v => ({
                        name: v.name || v.voiceURI || 'voice',
                        lang: v.language || v.lang || '',
                        _native: true
                    })).filter(v => v.name && v.lang);
                    setAvailableVoices(voices);
                } catch (err) {
                    console.warn('TextToSpeech.getSupportedVoices error:', err);
                    setAvailableVoices([]);
                }
            })();
            return;
        }

        if (!window.speechSynthesis) return;
        const load = () => {
            try {
                const all = window.speechSynthesis.getVoices();
                setAvailableVoices(all || []);
            } catch (e) { setAvailableVoices([]); }
        };
        load();
        window.speechSynthesis.addEventListener('voiceschanged', load);
        return () => window.speechSynthesis.removeEventListener('voiceschanged', load);
    }, []);

    // Filtrar voces por idioma seleccionado
    const filteredVoicesRaw = availableVoices.filter(v => {
        const prefix = langToBcp[language];
        if (!prefix || !v || !v.lang) return false;
        return String(v.lang).toLowerCase().startsWith(prefix);
    });
    // Deduplicar por nombre+lang
    const filteredVoices = Array.from(
        new Map(filteredVoicesRaw.map(v => [`${v.name}|${v.lang}`, v])).values()
    );

    // Lista de países traducidos al idioma del usuario, ordenada alfabéticamente.
    // Se recalcula solo cuando cambia el idioma.
    const sortedCountries = React.useMemo(() => getSortedCountries(language), [language]);

    // Regiones del país seleccionado. [] si el país no tiene datos de regiones —
    // en ese caso mostramos un input de texto libre como fallback.
    const regionsForCountry = React.useMemo(() => getRegions(country), [country]);

    // Cuando cambia el país, si la región actual no pertenece al nuevo país
    // (o el nuevo país no tiene lista), reseteamos la región a vacío para
    // que el usuario elija una nueva.
    useEffect(() => {
        if (!country) return;
        const regions = getRegions(country);
        if (regions.length > 0 && region && !regions.includes(region)) {
            setRegion('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country]);

    const playVoiceSample = async (voiceName) => {
        const samples = {
            'español': 'Hola, esta es mi voz', 'inglés': 'Hello, this is my voice',
            'francés': 'Bonjour, ceci est ma voix', 'alemán': 'Hallo, das ist meine Stimme',
            'portugués': 'Olá, esta é a minha voz', 'italiano': 'Ciao, questa è la mia voce',
            'euskera': 'Kaixo, hau da nire ahotsa', 'chino': '你好，这是我的声音',
            'japonés': 'こんにちは、これは私の声です', 'árabe': 'مرحبا هذا صوتي',
            'hindi': 'नमस्ते, यह मेरी आवाज़ है', 'ruso': 'Привет, это мой голос',
            'coreano': '안녕하세요, 제 목소리입니다', 'turco': 'Merhaba, bu benim sesim',
            'indonesio': 'Halo, ini suara saya'
        };
        const text = samples[language] || samples['español'];
        const selectedVoice = availableVoices.find(v => v.name === voiceName);

        if (Capacitor.isNativePlatform()) {
            try {
                await TextToSpeech.stop();
                const voiceIndex = availableVoices.findIndex(v => v.name === voiceName);
                await TextToSpeech.speak({
                    text,
                    lang: selectedVoice?.lang || langToBcp[language] || 'es-ES',
                    voice: voiceIndex >= 0 ? voiceIndex : undefined,
                    rate: 1.0,
                    pitch: 1.0,
                    volume: 1.0,
                    category: 'ambient'
                });
            } catch (err) {
                console.warn('TTS speak error:', err);
            }
            return;
        }

        if (!window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        if (selectedVoice) {
            utterance.voice = selectedVoice;
            utterance.lang = selectedVoice.lang;
        }
        window.speechSynthesis.speak(utterance);
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setAvatarFile(file);
        setAvatarPreview(prev => {
            if (prev && typeof prev === 'string' && prev.startsWith('blob:')) {
                try { URL.revokeObjectURL(prev); } catch {}
            }
            return URL.createObjectURL(file);
        });
    };

    useEffect(() => {
        return () => {
            if (avatarPreview && typeof avatarPreview === 'string' && avatarPreview.startsWith('blob:')) {
                try { URL.revokeObjectURL(avatarPreview); } catch {}
            }
        };
    }, [avatarPreview]);

    const handleSave = async () => {
        if (!name.trim()) { setError(t.profile.error.nameEmpty); return; }
        setSaving(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('userId', currentUser._id);
            formData.append('name', name.trim());
            formData.append('status', status);
            formData.append('language', language);
            formData.append('region', region);
            formData.append('country', country);
            formData.append('interests', JSON.stringify(interests));
            formData.append('voice', voice);
            if (avatarFile) formData.append('avatar', avatarFile);
            const token = localStorage.getItem('kaixo_token') || '';
            const response = await fetch(`${API_URL}/users/profile`, {
                method: 'PUT',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: formData
            });
            const data = await response.json();
            if (data.success) {
                onUpdate(data.user);
                localStorage.setItem('kaixo_user', JSON.stringify(data.user));
                // Notificar a otros componentes (sin reload completo, evita
                // pestañeo de la app y pérdida de scroll/contexto). Quien
                // necesite refrescar escucha kaixo:profile_updated.
                try { window.dispatchEvent(new CustomEvent('kaixo:profile_updated', { detail: { user: data.user } })); } catch {}
                onClose();
            } else {
                setError(data.error || t.profile.error.save);
            }
        } catch (err) {
            setError(t.profile.error.connection);
        }
        setSaving(false);
    };

    return (
        <div className="profile-overlay" onClick={onClose}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                <div className="profile-header">
                    <h3>{t.profile.title}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PlanBadge />
                        <button className="profile-close-btn" onClick={onClose}>
                            <Icons.Close size={18} color="var(--text-secondary)" />
                        </button>
                    </div>
                </div>

                <ImageViewer src={fullAvatar} onClose={() => setFullAvatar(null)} />
                <div className="profile-avatar-section">
                    <div
                        className="profile-avatar-preview"
                        onClick={() => { if (avatarPreview && !avatarPreview.startsWith('blob:')) setFullAvatar(avatarPreview); }}
                        style={avatarPreview && !avatarPreview.startsWith('blob:') ? { cursor: 'pointer' } : {}}
                    >
                        {avatarPreview
                            ? <img src={avatarPreview} alt="avatar" />
                            : <span>{name.charAt(0)}</span>
                        }
                    </div>
                    <label className="profile-avatar-btn">
                        <Icons.Camera size={16} color="white" /> {t.profile.changePhoto}
                        <input type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
                    </label>
                </div>

                <div className="profile-form">
                    <div className="profile-field">
                        <label>{t.profile.name}</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.profile.namePlaceholder} maxLength={50} />
                    </div>

                    <div className="profile-field">
                        <label>{t.profile.status}</label>
                        <input type="text" value={status} onChange={(e) => setStatus(e.target.value)} placeholder={t.profile.statusPlaceholder} maxLength={150} />
                    </div>

                    <div className="profile-field">
                        <label>{t.profile.language}</label>
                        <select value={language} onChange={(e) => setLanguage(e.target.value)} className="profile-select">
                            <option value="español">Español</option>
                            <option value="inglés">Inglés</option>
                            <option value="francés">Francés</option>
                            <option value="alemán">Alemán</option>
                            <option value="portugués">Portugués</option>
                            <option value="italiano">Italiano</option>
                            <option value="euskera">Euskera</option>
                            <option value="chino">Chino</option>
                            <option value="japonés">Japonés</option>
                            <option value="árabe">Árabe</option>
                            <option value="hindi">Hindi</option>
                            <option value="ruso">Ruso</option>
                            <option value="coreano">Coreano</option>
                            <option value="turco">Turco</option>
                            <option value="indonesio">Indonesio</option>
                        </select>
                    </div>

                    {/* País: dropdown alfabético con todos los países traducidos
                        al idioma del usuario. Se guarda como código ISO (ES, MX...)
                        y la etiqueta visible se traduce dinámicamente. */}
                    <div className="profile-field">
                        <label>{ui.profile?.country || 'País'}</label>
                        <select
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            className="profile-select"
                        >
                            <option value="">
                                {ui.profile?.selectCountry || 'Selecciona país…'}
                            </option>
                            {sortedCountries.map(c => (
                                <option key={c.code} value={c.code}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Región: dropdown que depende del país elegido. Si el país
                        tiene regiones registradas, mostramos un select. Si no
                        (o si todavía no se eligió país), mostramos un input
                        libre como fallback. */}
                    <div className="profile-field">
                        <label>{t.profile.region}</label>
                        {regionsForCountry.length > 0 ? (
                            <select
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                className="profile-select"
                            >
                                <option value="">{ui.profile.selectRegion}</option>
                                {regionsForCountry.map(r => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                placeholder={
                                    country
                                        ? (ui.profile?.regionFreePlaceholder || 'Tu provincia / región…')
                                        : (ui.profile?.selectCountryFirst || 'Elige primero un país')
                                }
                                disabled={!country}
                                maxLength={80}
                            />
                        )}
                    </div>

                    <div className="profile-field">
                        <label>{ui.profile?.interests || 'Intereses'} ({interests.length}/15)</label>
                        <button
                            type="button"
                            className="profile-select"
                            onClick={() => setShowInterestPicker(true)}
                            style={{ textAlign: 'left', cursor: 'pointer' }}
                        >
                            + {t.interestPicker?.openButton || ui.profile?.addInterest || 'Seleccionar intereses'}
                        </button>
                        {interests.length > 0 && (
                            <div className="profile-interests-grid" style={{ marginTop: 8 }}>
                                {interests.map(key => (
                                    <button
                                        key={key}
                                        type="button"
                                        className="profile-interest-chip active"
                                        onClick={() => removeInterest(key)}
                                    >
                                        {getInterestLabel(key, language)} ×
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="profile-field">
                        <label>{t.profile.voice}</label>
                        {filteredVoices.length >= 2 ? (
                            <div className="voice-selector-dropdown">
                                <select value={voice} onChange={(e) => setVoice(e.target.value)} className="profile-select">
                                    <option value="">{ui.profile.selectVoice}</option>
                                    {filteredVoices.map((v, i) => (
                                        <option key={`${v.name}-${v.lang}-${i}`} value={v.name}>
                                            {v.name}{v.lang ? ` (${v.lang})` : ''}
                                        </option>
                                    ))}
                                </select>
                                <button type="button" className="voice-play-btn" onClick={() => playVoiceSample(voice)} disabled={!voice}>
                                    <Icons.Speaker size={16} color="white" /> {ui.profile.testVoice}
                                </button>
                            </div>
                        ) : (
                            <div className="voice-selector">
                                <button type="button" className={`voice-option ${voice === 'female' ? 'active' : ''}`} onClick={() => setVoice('female')}>
                                    <Icons.Female size={18} color={voice === 'female' ? 'white' : 'var(--text-secondary)'} />
                                    {t.profile.female}
                                </button>
                                <button type="button" className={`voice-option ${voice === 'male' ? 'active' : ''}`} onClick={() => setVoice('male')}>
                                    <Icons.Male size={18} color={voice === 'male' ? 'white' : 'var(--text-secondary)'} />
                                    {t.profile.male}
                                </button>
                            </div>
                        )}
                    </div>

                    {error && <div className="profile-error">{error}</div>}

                    <button className="profile-save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? t.profile.saving : <><Icons.Save size={16} color="white" /> {t.profile.save}</>}
                    </button>
                </div>
            </div>
            {showInterestPicker && (
                <InterestPickerModal
                    value={interests}
                    max={15}
                    language={language}
                    accent="#ff6b35"
                    onSave={(next) => setInterests(next)}
                    onClose={() => setShowInterestPicker(false)}
                />
            )}
        </div>
    );
}

export default ProfileModal;